import { supabase } from '../../../lib/supabase';

import { analyticsService } from './analyticsService';

export interface FeedbackSurvey {
  id: string;
  title: string;
  description: string;
  questions: FeedbackQuestion[];
  isActive: boolean;
  targetAudience: 'all' | 'freemium' | 'premium' | 'churned' | 'beta';
  triggerConditions?: FeedbackTrigger[];
  startDate?: Date;
  endDate?: Date;
}

export interface FeedbackQuestion {
  id: string;
  type: 'rating' | 'text' | 'multiple_choice' | 'boolean';
  question: string;
  required: boolean;
  options?: string[]; // For multiple choice questions
  scale?: { min: number; max: number; labels?: string[] }; // For rating questions
}

export interface FeedbackTrigger {
  event: string;
  conditions?: Record<string, any>;
}

export interface FeedbackResponse {
  id: string;
  surveyId: string;
  userId: string;
  responses: Record<string, any>;
  submittedAt: Date;
  metadata?: Record<string, any>;
}

export interface UserFeedback {
  id: string;
  userId: string;
  type: 'survey' | 'interview' | 'support' | 'feature_request';
  content: string;
  rating?: number;
  category?: string;
  status: 'new' | 'in_progress' | 'resolved' | 'closed';
  submittedAt: Date;
  metadata?: Record<string, any>;
}

export class FeedbackService {
  /**
   * Submit user feedback
   */
  async submitFeedback(
    type: UserFeedback['type'],
    content: string,
    rating?: number,
    category?: string,
    metadata?: Record<string, any>
  ): Promise<string> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to submit feedback');
      }

      const { data, error } = await supabase
        .from('user_feedback')
        .insert({
          user_id: user.id,
          type,
          content,
          rating,
          category,
          status: 'new',
          metadata: metadata || {},
          submitted_at: new Date().toISOString()
        })
        .select('id')
        .single();

      if (error) {
        console.error('Failed to submit feedback:', error);
        throw error;
      }

      // Track feedback submission
      await analyticsService.track('feedback_submitted', {
        feedbackId: data.id,
        type,
        rating,
        category,
        hasContent: content.length > 0
      });

      return data.id;

    } catch (error) {
      console.error('Failed to submit feedback:', error);
      throw error;
    }
  }

  /**
   * Submit survey response
   */
  async submitSurveyResponse(
    surveyId: string,
    responses: Record<string, any>,
    metadata?: Record<string, any>
  ): Promise<string> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to submit survey response');
      }

      const { data, error } = await supabase
        .from('feedback_responses')
        .insert({
          survey_id: surveyId,
          user_id: user.id,
          responses,
          metadata: metadata || {},
          submitted_at: new Date().toISOString()
        })
        .select('id')
        .single();

      if (error) {
        console.error('Failed to submit survey response:', error);
        throw error;
      }

      // Track survey completion
      await analyticsService.track('survey_completed', {
        surveyId,
        responseId: data.id,
        questionCount: Object.keys(responses).length
      });

      return data.id;

    } catch (error) {
      console.error('Failed to submit survey response:', error);
      throw error;
    }
  }

  /**
   * Get active surveys for current user
   */
  async getActiveSurveys(userId?: string): Promise<FeedbackSurvey[]> {
    try {
      if (!userId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return [];
        userId = user.id;
      }

      // Get user's subscription status for targeting
      const { data: subscription } = await supabase
        .from('user_subscriptions')
        .select('subscription_type, status')
        .eq('user_id', userId)
        .eq('status', 'active')
        .single();

      const userType = subscription ? 'premium' : 'freemium';

      // Get active surveys targeting this user type
      const { data: surveys, error } = await supabase
        .from('feedback_surveys')
        .select('*')
        .eq('is_active', true)
        .in('target_audience', ['all', userType])
        .lte('start_date', new Date().toISOString())
        .gte('end_date', new Date().toISOString());

      if (error) {
        console.error('Failed to get active surveys:', error);
        return [];
      }

      // Filter out surveys user has already completed
      const { data: completedSurveys } = await supabase
        .from('feedback_responses')
        .select('survey_id')
        .eq('user_id', userId);

      const completedIds = new Set(completedSurveys?.map(r => r.survey_id) || []);

      return (surveys || [])
        .filter(survey => !completedIds.has(survey.id))
        .map(survey => ({
          id: survey.id,
          title: survey.title,
          description: survey.description,
          questions: survey.questions || [],
          isActive: survey.is_active,
          targetAudience: survey.target_audience,
          triggerConditions: survey.trigger_conditions,
          startDate: survey.start_date ? new Date(survey.start_date) : undefined,
          endDate: survey.end_date ? new Date(survey.end_date) : undefined
        }));

    } catch (error) {
      console.error('Failed to get active surveys:', error);
      return [];
    }
  }

  /**
   * Check if user should see in-app survey based on triggers
   */
  async shouldShowSurvey(triggerEvent: string, eventData?: Record<string, any>, userId?: string): Promise<FeedbackSurvey | null> {
    try {
      const surveys = await this.getActiveSurveys(userId);

      for (const survey of surveys) {
        if (survey.triggerConditions) {
          for (const trigger of survey.triggerConditions) {
            if (trigger.event === triggerEvent) {
              // Check if conditions are met
              if (this.evaluateTriggerConditions(trigger.conditions, eventData)) {
                // Track survey shown
                await analyticsService.track('survey_shown', {
                  surveyId: survey.id,
                  trigger: triggerEvent,
                  eventData
                });

                return survey;
              }
            }
          }
        }
      }

      return null;

    } catch (error) {
      console.error('Failed to check survey triggers:', error);
      return null;
    }
  }

  /**
   * Get feedback analytics
   */
  async getFeedbackAnalytics(startDate?: Date, endDate?: Date): Promise<{
    totalFeedback: number;
    averageRating: number;
    feedbackByType: Record<string, number>;
    feedbackByCategory: Record<string, number>;
    surveyResponses: number;
    completionRates: Record<string, number>;
  }> {
    try {
      const { data, error } = await supabase
        .rpc('get_feedback_analytics', {
          start_date: startDate?.toISOString() || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          end_date: endDate?.toISOString() || new Date().toISOString()
        });

      if (error) {
        console.error('Failed to get feedback analytics:', error);
        throw error;
      }

      return data || {
        totalFeedback: 0,
        averageRating: 0,
        feedbackByType: {},
        feedbackByCategory: {},
        surveyResponses: 0,
        completionRates: {}
      };

    } catch (error) {
      console.error('Failed to get feedback analytics:', error);
      throw error;
    }
  }

  /**
   * Create Phase 2 beta testing surveys
   */
  async createBetaSurveys(): Promise<void> {
    try {
      // Welcome survey for new beta users
      await this.createSurvey({
        title: 'Welcome to Kingdom Bible Quiz Beta',
        description: 'Help us improve by sharing your initial thoughts',
        questions: [
          {
            id: 'motivation',
            type: 'text',
            question: 'What motivated you to try Kingdom Bible Quiz?',
            required: true
          },
          {
            id: 'usage_intent',
            type: 'multiple_choice',
            question: 'How do you plan to use this app?',
            required: true,
            options: ['Personal Bible study', 'Family devotions', 'Church group activities', 'Teaching tool', 'Entertainment']
          },
          {
            id: 'prior_experience',
            type: 'rating',
            question: 'How familiar are you with Bible trivia apps?',
            required: false,
            scale: { min: 1, max: 5, labels: ['Not at all', 'Somewhat', 'Very familiar'] }
          }
        ],
        isActive: true,
        targetAudience: 'beta',
        triggerConditions: [{ event: 'user_registered' }]
      });

      // Post-quiz feedback survey
      await this.createSurvey({
        title: 'Quiz Experience Feedback',
        description: 'Tell us about your quiz experience',
        questions: [
          {
            id: 'quiz_difficulty',
            type: 'rating',
            question: 'How would you rate the difficulty of the questions?',
            required: true,
            scale: { min: 1, max: 5, labels: ['Too easy', 'Just right', 'Too hard'] }
          },
          {
            id: 'upgrade_prompt',
            type: 'rating',
            question: 'How likely are you to upgrade to premium?',
            required: true,
            scale: { min: 1, max: 10, labels: ['Not at all likely', 'Extremely likely'] }
          },
          {
            id: 'barriers_to_upgrade',
            type: 'multiple_choice',
            question: 'What prevents you from upgrading? (Select all that apply)',
            required: false,
            options: ['Price too high', 'Not enough value', 'Prefer free version', 'Need to try more first', 'Payment method issues']
          },
          {
            id: 'improvements',
            type: 'text',
            question: 'What would make you more likely to upgrade?',
            required: false
          }
        ],
        isActive: true,
        targetAudience: 'freemium',
        triggerConditions: [{ event: 'daily_limit_reached', conditions: { quiz_count: 5 } }]
      });

      console.log('Beta surveys created successfully');

    } catch (error) {
      console.error('Failed to create beta surveys:', error);
    }
  }

  /**
   * Private method to create survey
   */
  private async createSurvey(survey: Omit<FeedbackSurvey, 'id'>): Promise<string> {
    const { data, error } = await supabase
      .from('feedback_surveys')
      .insert({
        title: survey.title,
        description: survey.description,
        questions: survey.questions,
        is_active: survey.isActive,
        target_audience: survey.targetAudience,
        trigger_conditions: survey.triggerConditions,
        start_date: survey.startDate?.toISOString() || new Date().toISOString(),
        end_date: survey.endDate?.toISOString()
      })
      .select('id')
      .single();

    if (error) {
      console.error('Failed to create survey:', error);
      throw error;
    }

    return data.id;
  }

  /**
   * Private method to evaluate trigger conditions
   */
  private evaluateTriggerConditions(conditions?: Record<string, any>, eventData?: Record<string, any>): boolean {
    if (!conditions || !eventData) return true;

    for (const [key, expectedValue] of Object.entries(conditions)) {
      if (eventData[key] !== expectedValue) {
        return false;
      }
    }

    return true;
  }
}

// Singleton instance
export const feedbackService = new FeedbackService();

// Default export
export default feedbackService;